import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";

export default function ExclusiveProductPage() {
  const products = [
    {
      name: "Kanak Vastra",
      description:
        "A handwoven saree embodying elegance, comfort, and tradition, crafted with the finest threads of heritage.",
      image: "https://manyavar.scene7.com/is/image/manyavar/SB17229_414-PINK_101.15764_27-05-2024-11-33:650x900?&dpr=on,2",
    },
    {
      name: "Neelambari",
      description:
        "Draped in royalty, this deep blue handloom saree reflects the timeless beauty of craftsmanship.",
      image: "https://manyavar.scene7.com/is/image/manyavar/SB17229_414-PINK_301.15788_27-05-2024-11-35:650x900?&dpr=on,2",
    },
    {
      name: "Chandrika",
      description:
        "Soft, radiant, and enchanting—this golden-hued handloom saree embodies grace and charm.",
      image: "https://manyavar.scene7.com/is/image/manyavar/SB17229_414-PINK_601.15776_27-05-2024-11-34:650x900",
    },
    {
      name: "Twarita",
      description:
        "Soft, radiant, and enchanting—this golden-hued handloom saree embodies grace and charm.",
      image: "https://manyavar.scene7.com/is/image/manyavar/SB16909_433-RAMA+GREEN_101.6181_25-12-2024-17-07:650x900?&dpr=on,2",
    },
  ];

  const [index, setIndex] = useState(0);

  const nextProduct = () => setIndex((prev) => (prev + 1) % products.length);
  const prevProduct = () =>
    setIndex((prev) => (prev - 1 + products.length) % products.length);

  const getProductImage = (offset) => {
    const newIndex = (index + offset + products.length) % products.length;
    return products[newIndex].image;
  };

  // Ref for the product section
  const productRef = useRef(null);

  // Scroll progress for the product section
  const { scrollYProgress } = useScroll({
    target: productRef,
    offset: ["start end", "end start"], // Trigger when the section enters and exits the viewport
  });

  // Transformations for the left image
  const leftImageOpacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const leftImageRotateY = useTransform(scrollYProgress, [0, 1], [-45, 0]);
  const leftImageTranslateX = useTransform(scrollYProgress, [0, 1], [0, -130]);
  const leftImageScale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);

  // Transformations for the right image
  const rightImageOpacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const rightImageRotateY = useTransform(scrollYProgress, [0, 1], [45, 0]);
  const rightImageTranslateX = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const rightImageScale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);

  // Transformations for the main image
  const mainImageOpacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const mainImageScale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-900 via-black to-blue-900 text-white px-6 overflow-hidden">
      {/* Frosted Glass Gradient Patches */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-gradient-to-r from-gray-500 to-gray-700 opacity-20 blur-3xl -top-32 -left-32 rounded-full"></div>
        <div className="absolute w-96 h-96 bg-gradient-to-r from-blue-400 to-blue-600 opacity-20 blur-3xl top-1/2 -left-48 rounded-full"></div>
        <div className="absolute w-96 h-96 bg-gradient-to-r from-pink-400 to-pink-600 opacity-20 blur-3xl -bottom-32 -right-32 rounded-full"></div>
        <div className="absolute w-96 h-96 bg-gradient-to-r from-white to-gray-300 opacity-20 blur-3xl top-1/2 -right-48 rounded-full"></div>
      </div>

      {/* Hero Section */}
      <motion.section
        className="relative w-full h-[70vh] flex items-center justify-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background Landscape Image */}
        <motion.img
          src="https://images.unsplash.com/photo-1615992174118-9b8e9be025e7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
          alt="Collection Landscape"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        />

        {/* Logo and Collection Name */}
        <motion.div
          className="relative z-10 text-center"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <img
            src="/path/to/collection-logo.png" // Replace with your logo path
            alt="Collection Logo"
            className="w-32 h-32 mx-auto mb-4"
          />
          <h1 className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-600">
            The Heritage Collection
          </h1>
          <p className="mt-4 text-lg text-gray-300">
            A timeless collection of handwoven sarees, crafted with love and tradition.
          </p>
        </motion.div>
      </motion.section>

      {/* Product Section */}
      <motion.section
        ref={productRef}
        className="relative z-10 max-w-3xl text-center mb-16 mt-52"
      >
        <motion.h1
          className="text-4xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-600"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {products[index].name}
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          {products[index].description}
        </motion.p>

        <motion.div
          className="mt-6 flex items-center justify-center relative"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        >
          {/* Left Side Image */}
          <motion.img
            src={getProductImage(-1)}
            alt="Left Side Image"
            className="w-[300px] h-[400px] object-cover rounded-lg border-2 border-gray-200 shadow-lg absolute left-2"
            style={{
              opacity: leftImageOpacity,
              rotateY: leftImageRotateY,
              translateX: leftImageTranslateX,
              scale: leftImageScale,
              transformStyle: "preserve-3d",
              perspective: 1000,
            }}
          />

          {/* Main Image */}
          <motion.img
            src={products[index].image}
            alt={products[index].name}
            className="w-[400px] h-[500px] object-cover rounded-lg border-2 border-gray-200 shadow-xl shadow-neutral-700 z-10"
            style={{
            //   opacity: mainImageOpacity,
            //   scale: mainImageScale,
            }}
            whileHover={{ scale: 1.05 }}
          />

          {/* Right Side Image */}
          <motion.img
            src={getProductImage(1)}
            alt="Right Side Image"
            className="w-[300px] h-[400px] object-cover rounded-lg border-2 border-gray-200 shadow-lg absolute right-2"
            style={{
              opacity: rightImageOpacity,
              rotateY: rightImageRotateY,
              translateX: rightImageTranslateX,
              scale: rightImageScale,
              transformStyle: "preserve-3d",
              perspective: 1000,
            }}
          />

          {/* Navigation Buttons */}
          <button
            onClick={prevProduct}
            className="absolute left-0 text-gray-400 hover:text-white text-2xl z-20"
          >
            &#10094;
          </button>
          <button
            onClick={nextProduct}
            className="absolute right-0 text-gray-400 hover:text-white text-2xl z-20"
          >
            &#10095;
          </button>
        </motion.div>
      </motion.section>

      {/* Additional Sections */}
      <section className="relative z-10 max-w-4xl text-center mb-16">
        <motion.h2
          className="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-600"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          The Essence of Handloom
        </motion.h2>
        <motion.p
          className="text-lg text-gray-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          Handloom sarees are a timeless tradition, woven with precision and care by artisans. Every piece tells a story of culture, comfort, and craftsmanship.
        </motion.p>
      </section>

      <section className="relative z-10 max-w-4xl text-center mb-16">
        <motion.h2
          className="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-600"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Unparalleled Comfort
        </motion.h2>
        <motion.p
          className="text-lg text-gray-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          Our sarees are made from the softest natural fibers, ensuring comfort and elegance throughout the day, whether for casual wear or grand occasions.
        </motion.p>
      </section>

      <section className="relative z-10 max-w-4xl text-center mb-16">
        <motion.h2
          className="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-600"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          A Heritage to Treasure
        </motion.h2>
        <motion.p
          className="text-lg text-gray-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          Every weave carries centuries of heritage, reflecting the artistry and dedication of our skilled artisans. Wear a piece of history with pride.
        </motion.p>
      </section>
    </div>
  );
}